import {
  put,
  call,
  takeLatest,
  select,
  take,
  fork,
  cancel,
  delay,
  getContext,
} from 'redux-saga/effects';
import * as _ from 'lodash-es';
import {
  setDocuments,
  setPaginatorPageCount,
  setRepositoryLoading,
  setPaginatorPage,
  updateFilterSubtree,
  setFoldersCount,
  resetAdvancedSearch,
} from '../../Actions/repository';
import * as actions from '../../Actions/types';
import {
  getActiveFolder,
  getComputedFilter,
  getOrderBy,
} from '../../Selectors/repository';
import { parseGatewayResources } from '../../Utils/documentUtils';
import callApi from '../Effects/callApi';
import { notify } from '../../Actions/ui';
import { exportDocumentsExcel, exportTitleUrlExcel } from './exportExcelSaga';
import { toQueryProperties } from '../../Utils/projectUtils';
import complete from '../Effects/complete';
import { getAppearance } from '../../Selectors/ui';
import {
  EXPORT_SEARCHED_DOCUMENTS_EXCEL,
  EXPORT_SEARCHED_URLS_EXCEL,
  EXPORT_URLS_EXCEL,
} from '../../Actions/types/repository';
import { getSearchOperation } from 'cosmos-core';
import repository from '../../Api/repository';
import {
  LOCAL_STORAGE_TEMPORARY_BUCKET,
  FOLDER_TYPE_TEMPORARY_BUCKET,
  LOCAL_STORAGE_SHOW_EMPTY,
} from '../../Utils/constants';
import {
  operation,
  orOperation,
  queryOperand,
  select as sqlSelect,
} from 'cosmos-config/nql';
import repositoryApi from '../../Api/repository.js';
import { isRecentlyUploadedFilter } from '../../Utils/searchUtils.js';
import { fetchUserSettings } from '../userSettingsSaga';
import { getTableSorting } from '../../Constants/userSettingsScope';
import { getWithExpiry } from '../../Utils';

// Reusable NQL operation to exclude linked documents
const getExcludeLinkedDocsOperation = () =>
  operation(
    'or',
    operation('isNull', queryOperand('parentdocumentid')),
    operation('in', queryOperand('resourcetype'), 2),
    operation(
      'and',
      operation('eq', queryOperand('resourcetype'), 3),
      operation(
        'or',
        operation('isNull', queryOperand('parentdocumentid')),
        operation('eq', queryOperand('parentdocumentid'), queryOperand('Resourceid_Copy'))
      )
    )
  );

const getFolderTypeFilter = (filter) => {
  const { foldertype } = filter;
  if (
    foldertype == null ||
    (Array.isArray(foldertype) && foldertype.length === 0)
  ) {
    return _.omit(
      {
        ...filter,
        resourcetype: 2,
      },
      'foldertype'
    );
  }

  return {
    ...filter,
    resourcetype: 1,
  };
};

function* getSearchOptions(properties) {
  const filter = yield select(getComputedFilter);
  const project = yield select((state) => state.project);
  const projectName = project?.openedProjectCode;
  const filterConfig = yield select((state) => state.repository.filterConfig);

  const filterCopy = _.cloneDeep(filter);

  if (filterCopy.foldertype === FOLDER_TYPE_TEMPORARY_BUCKET) {
    const storedBuckets = localStorage.getItem(LOCAL_STORAGE_TEMPORARY_BUCKET);
    const parsedBuckets = storedBuckets ? JSON.parse(storedBuckets) : {};

    filterCopy.foldertype = null;
    filterCopy.identifier = parsedBuckets[projectName] || [];
  }

  const DEFAULT_SHOW_EMPTY = true;
  const DEFAULT_SHOW_EMPTY_EXPIRY = (() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(7, 0, 0, 0);
    return tomorrow;
  })(); // Next day at 7am

  const showEmpty = getWithExpiry(
    LOCAL_STORAGE_SHOW_EMPTY,
    DEFAULT_SHOW_EMPTY,
    DEFAULT_SHOW_EMPTY_EXPIRY
  );

  if (
    !showEmpty &&
    !!filterCopy.foldertype &&
    filterCopy.foldertype !== FOLDER_TYPE_TEMPORARY_BUCKET
  ) {
    filterCopy.DocumentCount = 0;
  }

  const advancedFilter = _.mapValues(
    getFolderTypeFilter(filterCopy),
    (v, propertyName) => {
      if (_.has(v, 'value') && _.has(v, 'operator')) {
        return v;
      }

      if (Array.isArray(v) && v.length > 1) {
        return {
          value: v,
          operator: filterConfig[propertyName]?.multiMode,
        };
      }

      return v;
    }
  );

  const substructure = yield select((state) => state.repository.substructure);
  const searchQuery = yield select((state) => state.repository.searchQuery);
  const searchProperties = yield select(
    (state) => state.repository.searchProperties
  );
  const { isFulltext, signatureStatus } = yield select(
    (state) => state.repository.searchOptions
  );

  const openedDocumentId = yield select(
    (state) => state.document.openedDocumentId
  );

  const searchOperation = getSearchOperation(advancedFilter, properties, {
    substructure,
    searchQuery,
    fulltextSearch: isFulltext,
    searchProperties: searchProperties.map((p) => p.name),
    signatureStatus,
  });

  // Combine with the current search operation and the reusable exclusion filter
  const finalOperation = operation('and', searchOperation, getExcludeLinkedDocsOperation());

  const forbiddenQueryPatterns = [/\*\*/g];

  let sanitizedSearchQuery = '';
  if (searchQuery?.trim().length) {
    forbiddenQueryPatterns.forEach((pattern) => {
      sanitizedSearchQuery = searchQuery.replace(pattern, '');
    });
  }

  let fullTextSearchQuery = null;

  if (isFulltext && searchQuery?.trim().length) {
    fullTextSearchQuery = openedDocumentId
      ? sanitizedSearchQuery
      : processFullTextQuery(sanitizedSearchQuery);
  }
  return {
    searchQuery: fullTextSearchQuery,
    operation: finalOperation,
  };
}

function* fetchDocuments(projectCode, folderId, paginator, force = false) {
  yield put(setRepositoryLoading(true, 'Loading documents'));

  const orderBy = yield select(getOrderBy);

  const projectService = yield getContext('projectService');
  const properties = yield call(projectService.getProperties);
  const requestedProps = toQueryProperties(properties);

  // const filterableProps = properties
  //   .filter((p) => p.filterable)
  //   .map((p) => p.name);

  if (requestedProps.length <= 0) {
    throw new Error('Empty requested properties array.');
  }

  const filter = yield select(getComputedFilter);

  const subtree = yield select((state) => state.repository.subtree);
  if (filter.resourcetype === 1 && subtree) {
    yield put(updateFilterSubtree(false));
  }

  // This logic is copied from RepositoryTableProvider to match the same scope for the user settings
  // this acts as a workaround because the data fetching is split into the legacy sagas and some handling
  // in the Context API state management
  const folder = yield select(getActiveFolder);
  const folderType = folder?.foldertype || null;
  const filterFolderType = filter?.foldertype || null;
  const sortingScope = getTableSorting(
    folderType,
    subtree,
    filterFolderType,
    null
  );
  const userSorting = yield call(fetchUserSettings, sortingScope, projectCode);

  try {
    const searchOptions = yield call(getSearchOptions, properties);
    const { items, count } = yield callApi(
      repository.searchRepository,
      projectCode,
      folderId,
      requestedProps,
      {
        ...searchOptions,
        paginator,
        orderBy: userSorting?.settingsData || orderBy,
        subtree,
      },
      force
    );

    if (items) {
      const documents = parseGatewayResources(items, properties);

      // const filterOptions = buildFilters(documents, filterableProps);
      // yield put(setFilterOptions(filterOptions));

      return { documents, count };
    }

    return { documents: [], count: 0 };
  } catch (err) {
    yield put(
      notify(
        `Loading documents failed with following reason: ${err.message}`,
        'error'
      )
    );
    console.error(err);
    return { documents: [], count: 0 };
  } finally {
    yield put(setRepositoryLoading(false));
  }
}

function* exportFolderExcel(projectCode, folderId, action) {
  const { documents } = yield call(fetchDocuments, projectCode, folderId);

  const { options } = action.payload;

  yield call(exportDocumentsExcel, documents, options.columns || []);
  yield complete(action);
}

function* repositorySearchSaga(projectCode, resourceId) {
  yield takeLatest(actions.repository.EXPORT_FOLDER_EXCEL, function* (action) {
    yield call(exportFolderExcel, projectCode, resourceId, action);
  });

  // eslint-disable-next-line func-names
  yield takeLatest(EXPORT_URLS_EXCEL, function* ({ allDocuments }) {
    if (allDocuments) {
      const { documents } = yield call(fetchDocuments, projectCode, resourceId);
      yield call(exportTitleUrlExcel, documents);
    }
  });

  yield takeLatest(EXPORT_SEARCHED_URLS_EXCEL, function* () {
    const documents = yield call(performAdvancedSearch, resourceId, true);
    yield call(exportTitleUrlExcel, documents);
  });

  yield takeLatest(EXPORT_SEARCHED_DOCUMENTS_EXCEL, function* (action) {
    const documents = yield call(performAdvancedSearch, resourceId, true);
    const { options } = action.payload;
    yield call(exportDocumentsExcel, documents, options.columns || []);
    yield complete(action);
  });

  yield takeLatest(
    actions.repository.GET_FOLDERS_COUNT,
    fetchFoldersCount,
    projectCode
  );

  yield takeLatest(
    actions.repository.SET_ADVANCED_SEARCH,
    performAdvancedSearch,
    resourceId,
    false
  );

  yield takeLatest(
    [
      actions.repository.SET_PAGINATOR_PAGE,
      actions.repository.SET_ORDERBY,
      actions.repository.SET_SUBSTRUCTURE,
    ],
    function* () {
      if (yield select((state) => state.repository.advancedSearch.nqlQuery)) {
        yield call(performAdvancedSearch, resourceId, false);
      }
    }
  );

  let lastTask;

  while (true) {
    const { type, payload } = yield take([
      actions.repository.REQUEST_DOCUMENTS,
      actions.repository.UPDATE_FILTER,
      actions.repository.SET_FILTER,
      actions.repository.CLEAR_FILTER,
      actions.repository.CLEAR_SEARCH_QUERY,
      actions.repository.UPDATE_SEARCH_QUERY,
      actions.repository.SET_ORDERBY,
      actions.filterFolder.SET_FILTER_FOLDER,
      actions.repository.SET_PAGINATOR_PAGE,
      actions.repository.SET_REPOSITORY_OPTIONS,
      actions.repository.REFRESH_REPOSITORY_SEARCH,
      actions.repository.SET_SEARCH_OPTIONS,
      actions.repository.SET_SUBSTRUCTURE,
    ]);

    if (lastTask) {
      yield cancel(lastTask);
    }

    if (
      (type === actions.repository.SET_SUBSTRUCTURE ||
        type === actions.repository.SET_PAGINATOR_PAGE ||
        type === actions.repository.SET_ORDERBY) &&
      (yield select((state) => state.repository.advancedSearch.nqlQuery))
    ) {
      continue;
    } else {
      yield put(resetAdvancedSearch());
    }

    // eslint-disable-next-line func-names
    lastTask = yield fork(function* () {
      if (
        ![
          actions.repository.SET_PAGINATOR_PAGE,
          actions.repository.REFRESH_REPOSITORY_SEARCH,
        ].includes(type)
      ) {
        yield put(setPaginatorPage(0));
      }

      yield delay(250);
      yield put(setDocuments([]));

      const { currentPage: number, pageSize } = yield select(
        (state) => state.repository.paginator
      );
      const filter = yield select((state) => state.repository.filter);

      const isRecentUpload = isRecentlyUploadedFilter(filter);

      const appearanceSettings = yield select(getAppearance);
      const size = appearanceSettings?.pageSize || pageSize;
      const options = yield select((state) => state.repository.options);
      const firstTenItemsPaginator = { number: 0, size: 10 };

      const paginator = isRecentUpload
        ? firstTenItemsPaginator
        : payload?.options?.disablePagination || options.disablePagination
          ? null
          : { number, size };

      const { documents, count } = yield call(
        fetchDocuments,
        projectCode,
        resourceId,
        paginator,
        type === actions.repository.REFRESH_REPOSITORY_SEARCH &&
          !!payload?.force
      );

      yield put(setDocuments(documents, count));
      yield put(setPaginatorPageCount(Math.ceil(count / size)));
    });
  }

  function* performAdvancedSearch(resourceId, noPaginator = false) {
    if (yield select((state) => state.repository.advancedSearch?.nqlQuery)) {
      try {
        yield put(setRepositoryLoading(true, 'Loading documents'));
        // Fetch resource ID and properties
        const projectService = yield getContext('projectService');
        const properties = yield call(projectService.getProperties);
        const requestedProps = toQueryProperties(properties);

        // Fetch order by and whereQuery from the state
        const orderBy = yield select(getOrderBy);
        const whereQuery = yield select(
          (state) => state.repository.advancedSearch?.nqlQuery
        );

        // Pagination settings
        const { currentPage, pageSize } = yield select(
          (state) => state.repository.paginator
        );
        const appearanceSettings = yield select(getAppearance);
        const size = appearanceSettings?.pageSize || pageSize;

        const options = yield select((state) => state.repository.options);

        const paginator = options?.disablePagination
          ? null
          : { number: currentPage + 1, size };

        // Build the NQL query
        const nqlQuery =
          !paginator || noPaginator
            ? sqlSelect(requestedProps)
                .where(whereQuery)
                .subtree(true)
                .orderBy(orderBy)
            : sqlSelect(requestedProps)
                .where(whereQuery)
                .subtree(true)
                .orderBy(orderBy)
                .paging(paginator);

        // Apply reusable exclusion filter for linked documents
        nqlQuery.filter(getExcludeLinkedDocsOperation());

        if (!(yield select((state) => state.repository.substructure))) {
          const substructureOperation = yield orOperation(
            operation('isNull', queryOperand('parentdocumentid')),
            operation(
              'eq',
              queryOperand('parentdocumentid'),
              queryOperand('Resourceid_Copy')
            )
          );
          nqlQuery.filter(substructureOperation);
        }

        const { items, count } = yield callApi(
          repositoryApi.queryFolderSearch,
          resourceId,
          nqlQuery.toString()
        );
        const documents = parseGatewayResources(items, properties).filter(
          (item) => item?.resourcetype !== 1
        );

        if (
          typeof noPaginator !== 'boolean' ||
          (typeof noPaginator === 'boolean' && !noPaginator)
        ) {
          yield put(setDocuments(documents, count));
          yield put(setPaginatorPageCount(Math.ceil(count / size)));
        }
        if (noPaginator) {
          return documents;
        }
      } catch (error) {
        console.error('Error performing advanced search:', error);
      } finally {
        yield put(setRepositoryLoading(false));
      }
    }
  }
}

function* fetchFoldersCount(projectCode) {
  const projectService = yield getContext('projectService');
  const folder = yield call(projectService.getFolders);
  const resourceId = yield select((state) => state.repository.folderId);
  const properties = yield call(projectService.getProperties);
  const requestedProps = toQueryProperties(properties);
  const searchQuery = yield select((state) => state.repository.searchQuery);

  const searchOperation = getSearchOperation({
    resourcetype: 1,
    foldertype: folder[0]?.name,
  });

  const { count } = yield callApi(
    repository.searchRepository,
    projectCode,
    resourceId,
    requestedProps,
    {
      ...{ operation: searchOperation, searchQuery },
    },
    false
  );

  yield put(setFoldersCount(count));
}

function processFullTextQuery(query) {
  const words = query.trim().split(/\s+/);
  if (words.length === 1) {
    return query.endsWith('~') ? query : query + '*';
  } else {
    return `"${query}"`;
  }
}

export default repositorySearchSaga;

